import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos } from "../lib/formato";
import { Cargando, Panel, Vacio } from "./piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Punto {
  periodo: string;
  aportaciones: number;
  barra: number;
  cuotas: number;
  total: number;
}

interface Ingresos {
  agrupar: "dia" | "mes";
  serie: Punto[];
  totales: { aportaciones: number; barra: number; cuotas: number; total: number };
}

const PERIODOS = [
  { clave: "semana", texto: "gi.dias7" as Clave, dias: 7, agrupar: "dia" as const },
  { clave: "mes", texto: "gi.dias30" as Clave, dias: 30, agrupar: "dia" as const },
  { clave: "anio", texto: "gi.meses12" as Clave, dias: 365, agrupar: "mes" as const },
];

/** Los tres origenes, en el orden en que se apilan y con su color. */
const CAPAS = [
  { clave: "aportaciones" as const, texto: "gi.dispensacion" as Clave, color: "var(--verde)" },
  { clave: "barra" as const, texto: "gi.barra" as Clave, color: "#c9a227" },
  { clave: "cuotas" as const, texto: "gi.cuotas" as Clave, color: "#6a9fd4" },
];

/**
 * De dónde viene el dinero, por días o por meses.
 *
 * Barras apiladas y no tres gráficos: lo que se quiere ver de un vistazo es
 * cuánto entró cada día y en que proporción, no comparar tres lineas sueltas.
 *
 * Sin librería de gráficos: son divs con altura en porcentaje. Una librería
 * pesaría más que toda esta pantalla junta para dibujar unos rectangulos.
 */
export default function GraficoIngresos() {
  const { t } = useIdioma();
  const [periodo, setPeriodo] = useState(PERIODOS[0]!);

  const datos = useQuery({
    queryKey: ["informe-ingresos", periodo.clave],
    queryFn: () => api<Ingresos>(`/informes/ingresos?dias=${periodo.dias}&agrupar=${periodo.agrupar}`),
  });

  const maximo = Math.max(1, ...(datos.data?.serie ?? []).map((p) => p.total));
  const hayAlgo = (datos.data?.totales.total ?? 0) > 0;

  return (
    <Panel
      titulo={t("gi.titulo")}
      acciones={
        <div className="fila" style={{ gap: 6 }}>
          {PERIODOS.map((p) => (
            <button
              key={p.clave}
              className={`boton ${periodo.clave === p.clave ? "" : "secundario"} pequeno`}
              onClick={() => setPeriodo(p)}
            >
              {t(p.texto)}
            </button>
          ))}
        </div>
      }
    >
      {datos.isLoading || !datos.data ? (
        <Cargando />
      ) : !hayAlgo ? (
        <Vacio>{t("gi.sinIngresos")}</Vacio>
      ) : (
        <>
          <div className="grafico">
            {datos.data.serie.map((punto) => (
              <div
                key={punto.periodo}
                className="columna-barra"
                title={
                  `${punto.periodo}: ${creditos(punto.total)}\n` +
                  CAPAS.map((c) => `${t(c.texto)}: ${creditos(punto[c.clave])}`).join("\n")
                }
              >
                <div className="pila-barra" style={{ height: `${(punto.total / maximo) * 100}%` }}>
                  {CAPAS.map((capa) =>
                    punto[capa.clave] > 0 ? (
                      <div
                        key={capa.clave}
                        style={{
                          height: `${(punto[capa.clave] / punto.total) * 100}%`,
                          background: capa.color,
                        }}
                      />
                    ) : null,
                  )}
                </div>
                <span className="etiqueta-barra">
                  {datos.data.agrupar === "dia" ? punto.periodo.slice(8) : punto.periodo.slice(5)}
                </span>
              </div>
            ))}
          </div>

          <div className="fila" style={{ gap: 16, marginTop: 12, flexWrap: "wrap" }}>
            {CAPAS.map((capa) => (
              <span key={capa.clave} className="fila pequeno" style={{ gap: 6 }}>
                <span className="muestra-color" style={{ background: capa.color }} />
                {t(capa.texto)}
                <strong className="tabular">{creditos(datos.data.totales[capa.clave])}</strong>
              </span>
            ))}
            <span className="fila pequeno" style={{ gap: 6, marginLeft: "auto" }}>
              {t("m.total")} <strong className="tabular">{creditos(datos.data.totales.total)}</strong>
            </span>
          </div>
        </>
      )}
    </Panel>
  );
}
